import { useEffect, useId, useRef } from 'react'
import { COLORS } from '../lib/theme'

export interface ConfirmModalProps {
  /** Heading — the question, e.g. "Are you sure you want to cancel?" */
  title: string
  /** Optional supporting line under the title. */
  description?: string
  /** Confirm action label. Defaults to "Yes". */
  confirmLabel?: string
  /** Dismiss action label. Defaults to "No". */
  cancelLabel?: string
  /** Visual tone of the confirm button. */
  tone?: 'primary' | 'danger'
  /** Confirm the action (e.g. discard/cancel). */
  onConfirm: () => void
  /** Dismiss — stay where you are ("No", Escape, backdrop click). */
  onCancel: () => void
}

/**
 * Reusable, accessible confirmation modal — the in-app replacement for
 * window.confirm. Scrim + centered card matching the JoinHuntModal pattern;
 * role="dialog"/aria-modal, labelled by its title and described by its body.
 * Escape and backdrop click both dismiss (= the cancel / "No" action). Focus
 * moves into the dialog on open (onto the dismiss button — the safe default for
 * a destructive prompt), is trapped within it, and returns to the
 * previously-focused element on close.
 */
export function ConfirmModal({
  title,
  description,
  confirmLabel = 'Yes',
  cancelLabel = 'No',
  tone = 'primary',
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const titleId = useId()
  const descId = useId()
  const cardRef = useRef<HTMLDivElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    cancelRef.current?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onCancel()
        return
      }
      if (e.key === 'Tab') {
        const focusables = cardRef.current?.querySelectorAll<HTMLElement>('button')
        if (!focusables || focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      // Restore focus to whatever opened the modal, so keyboard users aren't
      // dropped at the top of the document.
      previouslyFocused?.focus?.()
    }
  }, [onCancel])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        ref={cardRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.surface,
          borderRadius: 12,
          padding: 24,
          maxWidth: 360,
          width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}
      >
        <p
          id={titleId}
          style={{ margin: description ? '0 0 8px' : 0, fontSize: 16, fontWeight: 700, color: COLORS.onSurface }}
        >
          {title}
        </p>
        {description && (
          <p id={descId} style={{ margin: '0 0 20px', fontSize: 14, color: COLORS.onSurfaceVariant }}>
            {description}
          </p>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: description ? 0 : 20 }}>
          <button ref={cancelRef} className="wh-btn wh-btn-text" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            className={`wh-btn ${tone === 'danger' ? 'wh-btn-danger' : 'wh-btn-primary'}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
